import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

const StrategyContext = React.createContext({});

const StrategyProvider = ({ children }) => {
  const [totalAsset, setTotalAsset] = useState("1000");
  const [liquidationPenalty, setLiquidationPenalty] = useState("0");
  const [ratioOfDeposit, setRatioOfDeposit] = useState("80");
  const [ratioOfBorrow, setRatioOfBorrow] = useState("50");
  const [averageAmount, setAverageAmount] = useState("330");
  const [averageNumber, setAverageNumber] = useState("2");
  const [ageOfWallet, setAgeOfWallet] = useState("100");
  const [trustworthinessOfPossessingAssets, setTrustworthinessOfPossessingAssets] = useState("800");
  const [numberOfMonitoringDays, setNumberOfMonitoringDays] = useState("30");

  const errorTotalAsset = useMemo(() => {
    return totalAsset < 0 || totalAsset === "";
  }, [totalAsset]);

  const helperTextTotalAsset = useMemo(() => {
    if (errorTotalAsset) return "Total asset greater than or equal to 0";
    else return undefined;
  }, [errorTotalAsset]);

  const errorLiquidationPenalty = useMemo(() => {
    return liquidationPenalty < 0 || liquidationPenalty > 1000 || liquidationPenalty === "";
  }, [liquidationPenalty]);

  const helperTextLiquidationPenalty = useMemo(() => {
    if (errorLiquidationPenalty) return "Liquidation penalty must be between 0 and 1000";
    else return undefined;
  }, [errorLiquidationPenalty]);

  const errorRatioOfDeposit = useMemo(() => {
    return ratioOfDeposit < 0 || ratioOfDeposit > 300 || ratioOfDeposit === "";
  }, [ratioOfDeposit]);

  const helperTextRatioOfDeposit = useMemo(() => {
    if (errorRatioOfDeposit) return "Ratio of Deposit to Total asset must be between 0 and 300";
    else return undefined;
  }, [errorRatioOfDeposit]);

  const errorRatioOfBorrow = useMemo(() => {
    return (
      (ratioOfDeposit > 0 &&
        Math.max(0, parseFloat(((ratioOfDeposit - 100) / ratioOfDeposit) * 100 + 1).toFixed(2)) > ratioOfBorrow) ||
      ratioOfBorrow > 80 ||
      ratioOfBorrow < 0 ||
      ratioOfBorrow === ""
    );
  }, [ratioOfDeposit, ratioOfBorrow]);

  const helperTextRatioOfBorrow = useMemo(() => {
    if (errorRatioOfBorrow)
      return `Ratio of Borrow to Deposit must be between ${Math.max(
        0,
        parseFloat(((ratioOfDeposit - 100) / ratioOfDeposit) * 100 + 1).toFixed(2)
      )} and 80`;
    else return undefined;
  }, [errorRatioOfBorrow, ratioOfDeposit]);

  const errorAverageAmount = useMemo(() => {
    return averageAmount < 0 || averageAmount === "";
  }, [averageAmount]);

  const helperTextAverageAmount = useMemo(() => {
    if (errorAverageAmount) return "Average amount of transactions per day greater than or equal to 0";
    else return undefined;
  }, [errorAverageAmount]);

  const errorAverageNumber = useMemo(() => {
    return averageNumber < 0 || averageNumber === "";
  }, [averageNumber]);

  const helperTextAverageNumber = useMemo(() => {
    if (errorAverageNumber) return "Average number of transactions per day greater than or equal to 0";
    else return undefined;
  }, [errorAverageNumber]);

  const errorAgeOfWallet = useMemo(() => {
    return ageOfWallet < 0 || ageOfWallet === "";
  }, [ageOfWallet]);

  const helperTextAgeOfWallet = useMemo(() => {
    if (errorAgeOfWallet) return "Age of wallet greater than or equal to 0";
    else return undefined;
  }, [errorAgeOfWallet]);

  const errorTrustworthinessOfPossessingAssets = useMemo(() => {
    return (
      0 > trustworthinessOfPossessingAssets ||
      trustworthinessOfPossessingAssets > 1000 ||
      trustworthinessOfPossessingAssets === ""
    );
  }, [trustworthinessOfPossessingAssets]);

  const helperTextTrustworthinessOfPossessingAssets = useMemo(() => {
    if (errorTrustworthinessOfPossessingAssets)
      return "Trustworthiness of possessing assets must be between 0 and 1000";
    else return undefined;
  }, [errorTrustworthinessOfPossessingAssets]);

  const errorNumberOfMonitoringDays = useMemo(() => {
    return numberOfMonitoringDays < 1 || numberOfMonitoringDays > 30 || numberOfMonitoringDays === "";
  }, [numberOfMonitoringDays]);

  const helperTextNumberOfMonitoringDays = useMemo(() => {
    if (errorNumberOfMonitoringDays) return "Number of monitoring days must be between 1 and 30";
    else return undefined;
  }, [errorNumberOfMonitoringDays]);

  const [disableEstimateYourCreditScore, setDisableEstimateYourCreditScore] = useState(false);

  useEffect(() => {
    setDisableEstimateYourCreditScore(
      errorTotalAsset ||
        errorLiquidationPenalty ||
        errorRatioOfDeposit ||
        errorRatioOfBorrow ||
        errorAverageAmount ||
        errorAverageNumber ||
        errorAgeOfWallet ||
        errorTrustworthinessOfPossessingAssets ||
        errorNumberOfMonitoringDays
    );
  }, [
    errorTotalAsset,
    errorLiquidationPenalty,
    errorRatioOfDeposit,
    errorRatioOfBorrow,
    errorAverageAmount,
    errorAverageNumber,
    errorAgeOfWallet,
    errorTrustworthinessOfPossessingAssets,
    errorNumberOfMonitoringDays,
  ]);

  const contextData = useMemo(() => {
    return {
      data: {
        totalAsset,
        liquidationPenalty,
        ratioOfDeposit,
        ratioOfBorrow,
        averageAmount,
        averageNumber,
        ageOfWallet,
        trustworthinessOfPossessingAssets,
        numberOfMonitoringDays,
      },
      setData: {
        setTotalAsset,
        setLiquidationPenalty,
        setRatioOfDeposit,
        setRatioOfBorrow,
        setAverageAmount,
        setAverageNumber,
        setAgeOfWallet,
        setTrustworthinessOfPossessingAssets,
        setNumberOfMonitoringDays,
      },
      error: {
        errorTotalAsset,
        errorLiquidationPenalty,
        errorRatioOfDeposit,
        errorRatioOfBorrow,
        errorAverageAmount,
        errorAverageNumber,
        errorAgeOfWallet,
        errorTrustworthinessOfPossessingAssets,
        errorNumberOfMonitoringDays,
      },
      helperText: {
        helperTextTotalAsset,
        helperTextLiquidationPenalty,
        helperTextRatioOfDeposit,
        helperTextRatioOfBorrow,
        helperTextAverageAmount,
        helperTextAverageNumber,
        helperTextAgeOfWallet,
        helperTextTrustworthinessOfPossessingAssets,
        helperTextNumberOfMonitoringDays,
      },
      creditScore: {
        disableEstimateYourCreditScore,
        setDisableEstimateYourCreditScore,
      },
    };
  }, [
    totalAsset,
    liquidationPenalty,
    ratioOfDeposit,
    ratioOfBorrow,
    averageAmount,
    averageNumber,
    ageOfWallet,
    trustworthinessOfPossessingAssets,
    numberOfMonitoringDays,
    errorTotalAsset,
    errorLiquidationPenalty,
    errorRatioOfDeposit,
    errorRatioOfBorrow,
    errorAverageAmount,
    errorAverageNumber,
    errorAgeOfWallet,
    errorTrustworthinessOfPossessingAssets,
    errorNumberOfMonitoringDays,
    helperTextTotalAsset,
    helperTextLiquidationPenalty,
    helperTextRatioOfDeposit,
    helperTextRatioOfBorrow,
    helperTextAverageAmount,
    helperTextAverageNumber,
    helperTextAgeOfWallet,
    helperTextTrustworthinessOfPossessingAssets,
    helperTextNumberOfMonitoringDays,
    disableEstimateYourCreditScore,
  ]);

  return <StrategyContext.Provider value={contextData}>{children}</StrategyContext.Provider>;
};

StrategyProvider.propTypes = {
  children: PropTypes.node,
};

export default StrategyProvider;

export const useStrategyContext = () => React.useContext(StrategyContext);

export const useStrategySelector = (selectorFn) => {
  const result = useStrategyContext();

  return useMemo(() => selectorFn(result), [result]);
};
